import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Heart, Shield, Globe2, Users, BarChart3, Zap, Award, Building2 } from 'lucide-react';
import ChatbotWidget from '../components/ChatbotWidget';

const SEO_PAGES = {
  'corporate-giving-platform': {
    title: 'Corporate Giving Platform',
    h1: 'The Free Corporate Giving Platform That Runs Itself',
    subtitle: 'Connect your business with 1.8M+ verified charities. Launch campaigns, track ESG impact, and engage employees — all automated.',
    keywords: 'corporate giving platform, corporate philanthropy software, workplace giving',
    features: [
      { icon: Heart, title: 'Automated Giving Campaigns', desc: 'Create and launch donation campaigns in minutes. AI matches your company with the right charities based on your values.' },
      { icon: Users, title: 'Employee Engagement', desc: 'Gamified giving with badges, leaderboards, and social sharing. Employees track their impact and compete to give more.' },
      { icon: Shield, title: 'Employer Matching', desc: 'Automate donation matching with configurable ratios. Every employee dollar goes further with company backing.' },
      { icon: BarChart3, title: 'ESG Reporting', desc: 'Real-time ESG scores, impact reports, and analytics. Export-ready for compliance and stakeholder reporting.' },
    ],
    stats: [
      { value: '1.8M+', label: 'Verified Charities' },
      { value: '0%', label: 'Platform Fee' },
      { value: '<10 min', label: 'Setup Time' },
      { value: '87%', label: 'Avg Engagement' },
    ],
  },
  'employee-donation-matching': {
    title: 'Employee Donation Matching Software',
    h1: 'Automate Employee Donation Matching',
    subtitle: 'Double your team\'s charitable impact with automated employer matching. No spreadsheets, no manual tracking — just seamless generosity.',
    keywords: 'employee donation matching, employer matching gifts, corporate matching program',
    features: [
      { icon: Zap, title: 'Instant Matching', desc: 'Donations are matched in real-time. Employees see the multiplied impact immediately, driving more giving.' },
      { icon: Shield, title: 'Configurable Ratios', desc: 'Set 1:1, 2:1, or custom match ratios per campaign. Control budgets with caps and approval workflows.' },
      { icon: Globe2, title: '1.8M+ Charities', desc: 'Match donations to any verified US nonprofit. Powered by Every.org for secure, tax-deductible giving.' },
      { icon: BarChart3, title: 'Budget Control', desc: 'Set annual matching budgets, track utilization in real-time, and get alerts before limits are reached.' },
    ],
    stats: [
      { value: '2x', label: 'Impact Multiplier' },
      { value: '$0', label: 'Setup Cost' },
      { value: '100%', label: 'Tax Deductible' },
      { value: 'Instant', label: 'Match Processing' },
    ],
  },
  'esg-reporting-software': {
    title: 'ESG Reporting Software for Companies',
    h1: 'ESG Reporting That Builds Itself',
    subtitle: 'Track your environmental, social, and governance impact automatically. Generate stakeholder reports with one click.',
    keywords: 'ESG reporting software, ESG tracking platform, corporate sustainability reporting',
    features: [
      { icon: BarChart3, title: 'Real-Time ESG Scores', desc: 'Your ESG score updates automatically as employees donate, volunteer, and engage with impact campaigns.' },
      { icon: Award, title: 'Compliance-Ready Reports', desc: 'Export detailed impact reports for board meetings, investor updates, and regulatory filings.' },
      { icon: Globe2, title: 'Impact Websites', desc: 'AI-generated public impact portals showcase your company\'s giving story to customers and stakeholders.' },
      { icon: Building2, title: 'Brand Marketing', desc: 'Turn your ESG data into shareable content. Social media posts, PR stories, and impact certificates — all AI-generated.' },
    ],
    stats: [
      { value: 'A+', label: 'Avg ESG Rating' },
      { value: '1-click', label: 'Report Export' },
      { value: 'AI', label: 'Auto-Generated' },
      { value: '24/7', label: 'Live Tracking' },
    ],
  },
  'nonprofit-donation-platform': {
    title: 'Nonprofit Donation Platform',
    h1: 'Accept Donations From Corporate Partners',
    subtitle: 'Get listed on Altrunic and receive donations from companies running giving programs. Zero fees on bank transfers.',
    keywords: 'nonprofit donation platform, charity donation software, corporate charity partnerships',
    features: [
      { icon: Heart, title: 'Receive Corporate Donations', desc: 'Companies discover and fund your nonprofit through AI-powered matching based on mission alignment.' },
      { icon: Shield, title: 'Zero Platform Fees', desc: 'Altrunic and Every.org both take 0%. Bank transfers are fee-free; cards cost 2.2% + $0.30 unless the donor covers it.' },
      { icon: Zap, title: 'Automatic Tax Receipts', desc: 'Powered by Every.org — donors receive instant tax-deductible receipts. No paperwork for you.' },
      { icon: Users, title: 'Employee Giving Access', desc: 'Get in front of employees at companies running matching programs. More visibility, more recurring donors.' },
    ],
    stats: [
      { value: '0%', label: 'Platform Fees' },
      { value: 'Auto', label: 'Tax Receipts' },
      { value: '1.8M+', label: 'Charity Network' },
      { value: 'Instant', label: 'Donation Processing' },
    ],
  },
};

const SEOPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = SEO_PAGES[slug];

  useEffect(() => {
    if (page) {
      document.title = `${page.title} | Altrunic`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', page.subtitle);
    }
  }, [page]);

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Page not found</h1>
          <button onClick={() => navigate('/')} className="text-blue-600 hover:underline">Back to home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafbfc]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Altrunic" className="w-8 h-8 object-contain" />
            <span className="text-lg font-bold tracking-tight text-gray-900">Altrunic</span>
          </a>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-sm font-semibold text-gray-700 hover:text-gray-900 px-4 py-2">
              Log in
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 px-5 py-2.5 rounded-lg"
              data-testid="seo-get-started-btn"
            >
              Get started free
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight leading-tight" data-testid="seo-h1">
            {page.h1}
          </h1>
          <p className="mt-6 text-lg text-gray-500 max-w-2xl mx-auto">{page.subtitle}</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-all hover:shadow-lg flex items-center gap-2"
              data-testid="seo-hero-cta"
            >
              Start for free <ArrowRight className="w-4 h-4" />
            </button>
            <a href="/demo" className="px-8 py-4 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all">
              Try the demo
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-y border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {page.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-extrabold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {page.features.map((feature, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center mb-5">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Ready to get started?</h2>
          <p className="mt-4 text-gray-400">Free forever. Set up in under 10 minutes. No credit card required.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-8 px-10 py-4 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-100 transition-all hover:shadow-lg text-lg"
            data-testid="seo-bottom-cta"
          >
            Get started free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#fafbfc] border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Altrunic" className="w-6 h-6 object-contain" />
            <span className="text-sm font-semibold text-gray-700">Altrunic</span>
          </div>
          <div className="flex gap-6 text-sm text-gray-500">
            <a href="/solutions/corporate-giving-platform" className="hover:text-gray-900">Corporate Giving</a>
            <a href="/solutions/employee-donation-matching" className="hover:text-gray-900">Donation Matching</a>
            <a href="/solutions/esg-reporting-software" className="hover:text-gray-900">ESG Reporting</a>
            <a href="/demo" className="hover:text-gray-900">Demo</a>
          </div>
          <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} Altrunic. All rights reserved.</p>
        </div>
      </footer>

      <ChatbotWidget context="sales" />
    </div>
  );
};

export default SEOPage;
